import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { FeedbackProvider } from './components/feedback.tsx';
import { Layout } from './components/Layout.tsx';
import { SavedNotice } from './components/SavedNotice.tsx';
import { UnsavedChanges } from './components/UnsavedChanges.tsx';
import { LoadError, Loading, Page } from './components/ui.tsx';
import { Account, ChoosePassword } from './screens/Account.tsx';
import { ConnectClaude } from './screens/ConnectClaude.tsx';
import { Home } from './screens/Home.tsx';
import { Intake } from './screens/Intake.tsx';
import { Login, Verify } from './screens/Login.tsx';
import { ResetPassword } from './screens/ResetPassword.tsx';

// The Patterns page carries the book's words (spec "Pattern Mapping" §5.1): its own chunk, so they are not in the first
// load; the service worker caches it in the background with the rest of the app.
const Patterns = lazy(() => import('./screens/project/Patterns.tsx').then((m) => ({ default: m.Patterns })));
import { NewLead } from './screens/NewLead.tsx';
import { Decisions } from './screens/project/Decisions.tsx';
import { Banners } from './screens/project/Banners.tsx';
import { People } from './screens/project/People.tsx';
import { ProjectEntry, ClientPreview } from './screens/project/ProjectEntry.tsx';
import { Process, ProcessSetup } from './screens/project/Process.tsx';
import { ProjectSettings } from './screens/project/ProjectSettings.tsx';
import { ProjectSettingsFrame } from './screens/project/SettingsFrame.tsx';
import { ReviewFile, Reviews } from './screens/project/Reviews.tsx';
import { Files } from './screens/project/Files.tsx';
import { Timeline } from './screens/project/Timeline.tsx';
import { Budget } from './screens/project/Budget.tsx';
import { Board } from './screens/project/Board.tsx';
import { Mail, MailThread } from './screens/project/Mail.tsx';
import { Spent } from './screens/project/Spent.tsx';
import { ArtSettings } from './screens/settings/Art.tsx';
import { ConnectionsSettings } from './screens/settings/Connections.tsx';
import { DevelopmentSettings } from './screens/settings/Development.tsx';
import { SettingsFrame } from './screens/settings/Frame.tsx';
import { OrganizationSettings, SettingsHome } from './screens/settings/Settings.tsx';
import { TradesSettings } from './screens/settings/Trades.tsx';
import { TreeBuilderSettings } from './screens/settings/TreeBuilder.tsx';
import { Blog } from './screens/Blog.tsx';
import { About, Privacy, Terms } from './screens/Public.tsx';
import { DevelopmentBlog } from './screens/settings/DevelopmentBlog.tsx';
import { Triage } from './screens/Triage.tsx';
import { useMe } from './session.tsx';

function Private({ children }: { children: ReactNode }) {
  const { data: me, isLoading, error } = useMe();
  const location = useLocation();
  if (isLoading) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  // The API not answering (a restart, a deploy) is not being signed out: say so, never send them to sign in again.
  if (me === undefined && error) {
    return (
      <Page>
        <LoadError error={error} />
      </Page>
    );
  }
  // Signed out: back here afterwards, to the same page with the same question (?step=, ?set=).
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  // Signed in with a temporary password NSB set: their own comes first, wherever they were headed.
  if (me.user.mustChangePassword) return <ChoosePassword />;
  return <>{children}</>;
}

/** A Settings page: signed in, inside the Settings frame with its menu. */
function InSettings({ children }: { children: ReactNode }) {
  return (
    <Private>
      <SettingsFrame>{children}</SettingsFrame>
    </Private>
  );
}

/** A page of a project's Settings: signed in, inside the project's Settings frame with its menu (a client's Budget unframed). */
function InProjectSettings({ children }: { children: ReactNode }) {
  return (
    <Private>
      <ProjectSettingsFrame>{children}</ProjectSettingsFrame>
    </Private>
  );
}

/** Where Triage was: the reports are in Settings → Development tools now, the one named in the link still open. */
function ToReports() {
  const { hash } = useLocation();
  return <Navigate to={`/settings/reports${hash}`} replace />;
}

function ToProcess() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`/p/${projectId}/process`} replace />;
}

function ToHome() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`/p/${projectId}`} replace />;
}

export function App() {
  const { pathname } = useLocation();
  return (
    // The feedback layer is the outermost thing in the app: whatever breaks under it, a report can still be filed.
    <FeedbackProvider>
      <ErrorBoundary>
        <Layout>
          <ErrorBoundary key={pathname}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/verify" element={<Verify />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              {/* Where Claude sends a staff member to allow it to read the portal as them (spec "Claude Connector" §3.3). */}
              <Route path="/oauth/authorize" element={<Private><ConnectClaude /></Private>} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/" element={<Private><Home /></Private>} />
              <Route path="/leads/new" element={<Private><NewLead /></Private>} />
              <Route path="/intake" element={<Private><Intake /></Private>} />
              <Route path="/p/:projectId" element={<Private><ProjectEntry /></Private>} />
              {/* The three-step setup gave way to starting a lead (2026-10-07). */}
              <Route path="/p/:projectId/setup" element={<Private><ToHome /></Private>} />
              {/* The estimate came out until it is built again on the steps, and time until it is logged against them (2026-10-07; docs/backlog.md). */}
              <Route path="/p/:projectId/estimate" element={<Private><ToHome /></Private>} />
              <Route path="/p/:projectId/estimate/:revisionId" element={<Private><ToHome /></Private>} />
              <Route path="/p/:projectId/decisions" element={<Private><Decisions /></Private>} />
              <Route path="/p/:projectId/spent" element={<InProjectSettings><Spent /></InProjectSettings>} />
              <Route path="/p/:projectId/process" element={<Private><Process /></Private>} />
              {/* The eight tracks gave way to the process tree. */}
              <Route path="/p/:projectId/tracks" element={<Private><ToProcess /></Private>} />
              <Route path="/p/:projectId/time" element={<Private><ToHome /></Private>} />
              <Route path="/time" element={<Navigate to="/" replace />} />
              <Route path="/p/:projectId/people" element={<InProjectSettings><People /></InProjectSettings>} />
              <Route path="/p/:projectId/comments" element={<Private><ToHome /></Private>} />
              <Route path="/p/:projectId/reviews" element={<Private><Reviews /></Private>} />
              <Route path="/p/:projectId/reviews/files/:fileId" element={<Private><ReviewFile /></Private>} />
              <Route path="/p/:projectId/files" element={<Private><Files /></Private>} />
              <Route path="/p/:projectId/timeline" element={<Private><Timeline /></Private>} />
              <Route path="/p/:projectId/budget" element={<InProjectSettings><Budget /></InProjectSettings>} />
              <Route path="/p/:projectId/board" element={<Private><Board /></Private>} />
              <Route path="/p/:projectId/patterns" element={<Private><Suspense fallback={<Page><Loading /></Page>}><Patterns /></Suspense></Private>} />
              <Route path="/p/:projectId/mail" element={<Private><Mail /></Private>} />
              <Route path="/p/:projectId/mail/:threadId" element={<Private><MailThread /></Private>} />
              <Route path="/p/:projectId/banners" element={<InProjectSettings><Banners /></InProjectSettings>} />
              <Route path="/p/:projectId/settings" element={<InProjectSettings><ProjectSettings /></InProjectSettings>} />
              <Route path="/p/:projectId/settings/process" element={<InProjectSettings><ProcessSetup /></InProjectSettings>} />
              <Route path="/p/:projectId/client" element={<Private><ClientPreview /></Private>} />
              {/* Settings: five sections, each its own page; /settings opens on the first (or on Connections, for an OAuth answer). */}
              <Route path="/settings" element={<Private><SettingsHome /></Private>} />
              <Route path="/settings/organization" element={<InSettings><OrganizationSettings /></InSettings>} />
              {/* The process tree, managed the way the tree builder did it (Jason, 2026-10-06), an admin's, on any screen (routes/tree-builder.ts). */}
              <Route path="/settings/process" element={<InSettings><TreeBuilderSettings /></InSettings>} />
              <Route path="/settings/trades" element={<InSettings><TradesSettings /></InSettings>} />
              {/* The tree builder became the process tree (2026-10-06). */}
              <Route path="/settings/builder" element={<Navigate to="/settings/process" replace />} />
              {/* The house template retired into the process tree (2026-10-03). */}
              <Route path="/settings/template" element={<Navigate to="/settings/process" replace />} />
              <Route path="/settings/connections" element={<InSettings><ConnectionsSettings /></InSettings>} />
              <Route path="/settings/art" element={<InSettings><ArtSettings /></InSettings>} />
              <Route path="/settings/development" element={<InSettings><DevelopmentSettings /></InSettings>} />
              <Route path="/settings/reports" element={<InSettings><Triage /></InSettings>} />
              <Route path="/settings/blog" element={<InSettings><DevelopmentBlog /></InSettings>} />
              <Route path="/account" element={<Private><Account /></Private>} />
              <Route path="/triage" element={<ToReports />} />
              <Route
                path="*"
                element={
                  <Page>
                    <p className="py-10 text-ink-2">Nothing here.</p>
                  </Page>
                }
              />
            </Routes>
          </ErrorBoundary>
        </Layout>
      </ErrorBoundary>
      {/* Every change that lands says so, the same way on every page (api.ts `noticeWords`). */}
      <SavedNotice />
      {/* And a form's changes not yet saved say so too, with leaving asked first (P-29, src/unsaved.ts). */}
      <UnsavedChanges />
    </FeedbackProvider>
  );
}
